import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, ReferenceLine
} from 'recharts';
import { 
  Leaf, ArrowLeft, TrendingUp, TrendingDown, Minus, 
  Loader2, Calendar, Activity, Brain, Heart, Moon,
  Zap, Shield, BarChart3, PieChart as PieChartIcon, Target
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#4A6C58', '#6B8E7A', '#8FB09A', '#B3D1BB', '#D7F0DC', '#2D4A3E', '#1A3028'];

const CATEGORY_COLORS = {
  sleep: '#6366F1',
  energy: '#F59E0B',
  immunity: '#10B981',
  stress: '#8B5CF6',
  mood: '#EC4899',
  heart: '#EF4444',
  digestion: '#F97316',
  brain_focus: '#6366F1',
  weight: '#14B8A6',
  joints: '#06B6D4',
  hair_skin: '#EC4899',
  menopause: '#A855F7',
  fertility: '#F472B6'
};

export default function AnalyticsDashboardPage() {
  const navigate = useNavigate();
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [assessments, setAssessments] = useState([]);
  const [symptomTrends, setSymptomTrends] = useState(null);
  const [journalStats, setJournalStats] = useState(null);
  const [correlations, setCorrelations] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?redirect=/analytics');
    }
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const [assessRes, symptomsRes, journalRes, corrRes] = await Promise.all([
        axios.get(`${API}/assessments/compare`, { headers: getAuthHeader() }),
        axios.get(`${API}/symptoms/trends?days=30`, { headers: getAuthHeader() }),
        axios.get(`${API}/journal/stats?days=30`, { headers: getAuthHeader() }),
        axios.get(`${API}/journal/correlation/analysis?days=30`, { headers: getAuthHeader() })
      ]);
      
      setAssessments(assessRes.data.assessments || []);
      setSymptomTrends(symptomsRes.data);
      setJournalStats(journalRes.data.stats);
      setCorrelations(corrRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  }, [user, getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Prepare health scores chart data
  const healthScoresData = assessments.map((a, i) => {
    const scores = a.health_scores || {};
    return {
      name: format(parseISO(a.created_at), 'MMM d'),
      ...scores,
      date: a.created_at
    };
  }).reverse();

  // Prepare BMI tracking data
  const bmiData = assessments
    .filter(a => a.bmi)
    .map(a => ({
      name: format(parseISO(a.created_at), 'MMM d'),
      bmi: a.bmi,
      category: a.bmi_category,
      weight: a.weight_kg,
      date: a.created_at
    }))
    .reverse();
  
  // Calculate BMI trend
  const bmiTrend = bmiData.length >= 2 
    ? bmiData[bmiData.length - 1].bmi - bmiData[0].bmi 
    : 0;

  // Prepare symptom trends chart data
  const symptomChartData = symptomTrends?.trends?.slice(0, 5).map(trend => ({
    name: trend.symptom.replace(/_/g, ' '),
    average: trend.average,
    trend: trend.trend
  })) || [];

  // Prepare supplement effectiveness data
  const effectivenessData = correlations?.correlations?.slice(0, 6).map(c => ({
    name: c.supplement_name.length > 15 ? c.supplement_name.substring(0, 15) + '...' : c.supplement_name,
    fullName: c.supplement_name,
    score: Math.round(c.effectiveness_score),
    taken: c.avg_feeling_when_taken,
    notTaken: c.avg_feeling_when_not_taken
  })) || [];

  // Category distribution for pie chart
  const categoryData = assessments[0]?.recommended_supplements?.reduce((acc, supp) => {
    const cat = supp.category;
    const existing = acc.find(a => a.name === cat);
    if (existing) {
      existing.value++;
    } else {
      acc.push({ name: cat, value: 1 });
    }
    return acc;
  }, []) || [];

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="default" linkTo="/" />
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-[#1A1A1A] flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-primary" />
              Analytics Dashboard
            </h1>
            <p className="text-muted-foreground">Track your health journey and supplement effectiveness</p>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 bg-muted p-1 rounded-lg">
            {['overview', 'symptoms', 'supplements'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors ${
                  activeTab === tab ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Target className="w-4 h-4" />
              <span className="text-sm">Assessments</span>
            </div>
            <p className="text-2xl font-bold text-[#1A1A1A]">{assessments.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Activity className="w-4 h-4" />
              <span className="text-sm">Days Tracked</span>
            </div>
            <p className="text-2xl font-bold text-[#1A1A1A]">{symptomTrends?.total_logs || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Zap className="w-4 h-4" />
              <span className="text-sm">Current Streak</span>
            </div>
            <p className="text-2xl font-bold text-primary">{journalStats?.current_streak || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Avg Feeling</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{journalStats?.average_feeling || '-'}/10</p>
          </div>
        </div>

        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Health Scores Over Time */}
            {healthScoresData.length > 1 && (
              <div className="bg-white rounded-2xl border border-border p-6">
                <h2 className="font-semibold text-lg mb-4">Health Scores Over Time</h2>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={healthScoresData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      {Object.keys(CATEGORY_COLORS).slice(0, 5).map((cat, i) => (
                        <Line
                          key={cat}
                          type="monotone"
                          dataKey={cat}
                          stroke={CATEGORY_COLORS[cat]}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          name={cat.replace(/_/g, ' ')}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* BMI Tracking */}
            {bmiData.length > 0 && (
              <div className="bg-white rounded-2xl border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-lg">BMI Tracking</h2>
                  <div className="flex items-center gap-2">
                    {bmiTrend !== 0 && (
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                        bmiTrend < 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {bmiTrend < 0 ? (
                          <TrendingDown className="w-4 h-4" />
                        ) : (
                          <TrendingUp className="w-4 h-4" />
                        )}
                        <span>{Math.abs(bmiTrend).toFixed(1)}</span>
                      </div>
                    )}
                    <div className={`px-2 py-1 rounded-full text-sm font-medium ${
                      bmiData[bmiData.length - 1]?.category === 'Normal' ? 'bg-green-100 text-green-700' :
                      bmiData[bmiData.length - 1]?.category === 'Underweight' ? 'bg-blue-100 text-blue-700' :
                      bmiData[bmiData.length - 1]?.category === 'Overweight' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {bmiData[bmiData.length - 1]?.category}
                    </div>
                  </div>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={bmiData}>
                      <defs>
                        <linearGradient id="bmiFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#14B8A6" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#14B8A6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis 
                        domain={['dataMin - 2', 'dataMax + 2']} 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => value.toFixed(1)}
                      />
                      <Tooltip 
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-white p-3 rounded-lg border border-border shadow-lg">
                                <p className="font-medium text-sm">{label}</p>
                                <p className="text-lg font-bold text-[#14B8A6]">BMI: {payload[0].value}</p>
                                <p className="text-sm text-muted-foreground">
                                  Weight: {payload[0].payload.weight} kg
                                </p>
                                <p className={`text-xs font-medium mt-1 ${
                                  payload[0].payload.category === 'Normal' ? 'text-green-600' :
                                  payload[0].payload.category === 'Underweight' ? 'text-blue-600' :
                                  payload[0].payload.category === 'Overweight' ? 'text-amber-600' : 'text-red-600'
                                }`}>
                                  {payload[0].payload.category}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="bmi" 
                        stroke="#14B8A6" 
                        strokeWidth={2}
                        fill="url(#bmiFill)" 
                      />
                      {/* Reference lines for BMI categories */}
                      <ReferenceLine y={18.5} stroke="#3B82F6" strokeDasharray="3 3" />
                      <ReferenceLine y={25} stroke="#F59E0B" strokeDasharray="3 3" />
                      <ReferenceLine y={30} stroke="#EF4444" strokeDasharray="3 3" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-0.5 bg-blue-500" />
                    <span>Underweight (&lt;18.5)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-0.5 bg-amber-500" />
                    <span>Overweight (25)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-0.5 bg-red-500" />
                    <span>Obese (30)</span>
                  </div>
                </div>
              </div>
            )}

            {/* Two Column Layout */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Supplement Category Distribution */}
              {categoryData.length > 0 && (
                <div className="bg-white rounded-2xl border border-border p-6">
                  <h2 className="font-semibold text-lg mb-4">Supplement Categories</h2>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Symptom Averages */}
              {symptomChartData.length > 0 && (
                <div className="bg-white rounded-2xl border border-border p-6">
                  <h2 className="font-semibold text-lg mb-4">Symptom Averages (30 days)</h2>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={symptomChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis type="number" domain={[0, 10]} />
                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="average" fill="#4A6C58" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* No Data State */}
            {assessments.length === 0 && (
              <div className="bg-white rounded-2xl border border-border p-8 text-center">
                <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Start Your Health Journey</h2>
                <p className="text-muted-foreground mb-6">
                  Take your first assessment to see analytics and track progress over time.
                </p>
                <Link to="/quiz">
                  <Button className="btn-primary">Take Assessment</Button>
                </Link>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'symptoms' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Symptom Trends */}
            {symptomTrends?.trends?.length > 0 ? (
              <>
                <div className="bg-white rounded-2xl border border-border p-6">
                  <h2 className="font-semibold text-lg mb-6">Symptom Trends (30 days)</h2>
                  <div className="grid gap-4">
                    {symptomTrends.trends.map(trend => (
                      <div key={trend.symptom} className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium capitalize">{trend.symptom.replace(/_/g, ' ')}</p>
                            <div className="flex items-center gap-2">
                              {trend.trend === 'improving' && <TrendingUp className="w-4 h-4 text-green-500" />}
                              {trend.trend === 'worsening' && <TrendingDown className="w-4 h-4 text-red-500" />}
                              {trend.trend === 'stable' && <Minus className="w-4 h-4 text-gray-400" />}
                              <span className={`text-sm font-medium capitalize ${
                                trend.trend === 'improving' ? 'text-green-600' :
                                trend.trend === 'worsening' ? 'text-red-600' : 'text-gray-500'
                              }`}>
                                {trend.trend}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary rounded-full transition-all"
                                style={{ width: `${(trend.average / 10) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-semibold text-primary w-12">
                              {trend.average}/10
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Symptom History Chart */}
                <div className="bg-white rounded-2xl border border-border p-6">
                  <h2 className="font-semibold text-lg mb-4">Daily Symptom History</h2>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={symptomTrends.trends[0]?.data || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 12 }}
                          tickFormatter={(val) => format(parseISO(val), 'MMM d')}
                        />
                        <YAxis domain={[0, 10]} />
                        <Tooltip 
                          labelFormatter={(val) => format(parseISO(val), 'MMMM d, yyyy')}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="value" 
                          stroke="#4A6C58" 
                          fill="#4A6C58" 
                          fillOpacity={0.2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-2xl border border-border p-8 text-center">
                <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">No Symptom Data Yet</h2>
                <p className="text-muted-foreground mb-6">
                  Start tracking your daily symptoms to see trends and patterns.
                </p>
                <Link to="/symptoms">
                  <Button className="btn-primary">Track Symptoms</Button>
                </Link>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'supplements' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Supplement Effectiveness */}
            {effectivenessData.length > 0 ? (
              <>
                <div className="bg-white rounded-2xl border border-border p-6">
                  <h2 className="font-semibold text-lg mb-4">Supplement Effectiveness</h2>
                  <p className="text-sm text-muted-foreground mb-6">
                    Comparing your feeling on days you took each supplement vs days you didn't
                  </p>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={effectivenessData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis type="number" domain={[-50, 100]} />
                        <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                        <Tooltip 
                          formatter={(value, name) => [
                            name === 'score' ? `${value}%` : value,
                            name === 'score' ? 'Effectiveness' : name
                          ]}
                          labelFormatter={(label) => effectivenessData.find(d => d.name === label)?.fullName}
                        />
                        <Bar 
                          dataKey="score" 
                          fill="#4A6C58"
                          radius={[0, 4, 4, 0]}
                          label={{ position: 'right', fontSize: 12, fill: '#666' }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Feeling Comparison */}
                <div className="bg-white rounded-2xl border border-border p-6">
                  <h2 className="font-semibold text-lg mb-4">Feeling: Taken vs Not Taken</h2>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={effectivenessData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis domain={[0, 10]} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="taken" name="When Taken" fill="#4A6C58" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="notTaken" name="When Not Taken" fill="#D1D5DB" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Insights */}
                {correlations?.summary?.insights?.length > 0 && (
                  <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl border border-primary/20 p-6">
                    <h2 className="font-semibold text-lg mb-4">Key Insights</h2>
                    <ul className="space-y-3">
                      {correlations.summary.insights.map((insight, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          <span className="text-sm">{insight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-2xl border border-border p-8 text-center">
                <PieChartIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">No Journal Data Yet</h2>
                <p className="text-muted-foreground mb-6">
                  Log your supplement intake for at least 3 days to see effectiveness analysis.
                </p>
                <Link to="/journal">
                  <Button className="btn-primary">Start Journal</Button>
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
}
